// PolicySummary/index.tsx
import { Badge, Card } from "@/shared/components/ui";
import PolicySummaryContent from "./PolicySummaryContent";
import PolicySummaryFooter from "./PolicySummaryFooter";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/shared/utils";
import { CoverageUtils, PreventiveConfig, PreventiveOption } from "../../utils/CoverageUtils";

import { PetBreed } from "@spotpetinsurance/spot-types/entities/PetBreed";
import { Quote } from "@/shared/types/Quote.interface";
import { PetUnderwriterType } from "@spotpetinsurance/spot-types/entities/PetQuote";
import { PetModalConfig } from "../PetModal";
import { useAppLayerContext } from "@/shared/contexts/AppLayer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTag } from "@fortawesome/pro-regular-svg-icons";

export interface PolicySummaryConfig {
    enableAccordionToggle?: boolean;
    showPreDiscountPrice?: boolean;
    useGenericDiscountLabel?: boolean;
    /** When true, the per-discount row uses `discount.id` as its label instead of discount.name */
    showDiscountId?: boolean;
    isSingleCol?: boolean;
}

export const policySummaryVariants = cva("border-0", {
    variants: {
        variant: {
            default: "rounded-xl border border-stroke-primary bg-background-brand-b-secondary"
        }
    },
    defaultVariants: {
        variant: "default"
    }
});

export type PolicySummaryVariantProps = VariantProps<typeof policySummaryVariants>;

interface PolicySummaryProps extends PolicySummaryVariantProps {
    quote: Quote;
    breeds: PetBreed[] | undefined;
    config?: PolicySummaryConfig;
    wrapperClass?: string;
    cardClass?: string;
    preventiveOptions?: Record<string, PreventiveOption[]>;
    preventiveConfig?: PreventiveConfig;
    underwriter?: PetUnderwriterType;
    onPreventiveChange?: (value: string, petId?: string, applyToAll?: boolean) => Promise<void>;
    showAddPreventiveCare?: string;
    showAddPetUpsell?: string;
    showEditCoveragePet?: boolean;
    petModalConfig?: PetModalConfig;
    handleActivePetChange?: (value: string) => void;
}

const PolicySummary = (props: PolicySummaryProps) => {
    const { appState } = useAppLayerContext();
    const { partnerOffer } = appState;
    const { quote, breeds, wrapperClass, config, cardClass, variant = "default", preventiveOptions, preventiveConfig, underwriter, onPreventiveChange } = props;
    const policies = quote?.policies ?? [];
    const transactionFee = quote?.transactionFee?.value ?? 0;
    const totalPrice = CoverageUtils.calculatePolicyTotal(policies, transactionFee);
    // Sum of every policy's discounts, shown as positive "savings" in the footer
    const totalDiscount = Math.abs(policies.reduce((sum, policy) => sum + (policy.discounts?.reduce((acc, discount) => acc + (discount.amount ?? 0), 0) ?? 0), 0));
    const billingFrequency = quote?.billingInfo?.frequency ?? "monthly";
    const isAnnualBilling = billingFrequency === "yearly";

    const mergedConfig = {
        ...config,
        isSingleCol: config?.isSingleCol
    };

    return (
        <div className={cn("relative flex w-full flex-col", wrapperClass)}>
            <div className="grow">
                {partnerOffer && (
                    <div className="mx-3 flex justify-center">
                        <Badge variant="success" className="flex w-full justify-center gap-1 rounded-lg rounded-b-none px-3 py-2">
                            <FontAwesomeIcon icon={faTag} className="size-4" />
                            <p className="text-xs font-medium leading-4">
                                <strong>{partnerOffer.headline}</strong> {partnerOffer.offerText}
                            </p>
                        </Badge>
                    </div>
                )}
                <Card padding="default" className={cn(policySummaryVariants({ variant }), cardClass)}>
                    <PolicySummaryContent
                        quote={quote}
                        breeds={breeds}
                        config={mergedConfig}
                        isAnnualBilling={isAnnualBilling}
                        showAddPetUpsell={props.showAddPetUpsell}
                        showEditCoveragePet={props.showEditCoveragePet}
                        preventiveOptions={preventiveOptions}
                        preventiveConfig={preventiveConfig}
                        underwriter={underwriter}
                        onPreventiveChange={onPreventiveChange}
                        showAddPreventiveCare={props.showAddPreventiveCare}
                        petModalConfig={props.petModalConfig}
                        handleActivePetChange={props.handleActivePetChange}
                    />
                    <PolicySummaryFooter
                        policiesCount={policies.length}
                        transactionFee={transactionFee}
                        totalPrice={totalPrice}
                        totalDiscount={totalDiscount}
                        billingFrequency={billingFrequency}
                        isSingleCol={mergedConfig.isSingleCol}
                    />
                </Card>
            </div>
        </div>
    );
};

export default PolicySummary;
